import React, { useMemo } from 'react';
import { Switch, useLocation, Redirect } from 'react-router-dom';
import Spinner from 'components/common/spinner';
import Header from 'components/header';
import { useSelector } from 'react-redux';
import { isNewUserSelector, isSignedInSelector } from 'selectors/auth';
import MobileService from 'services/mobile';
import getDesktopProtectedRoutes from 'routes/desktop/protected';
import getDesktopUnprotectedRoutes from 'routes/desktop/unprotected';
import getTabletProtectedRoutes from 'routes/tablet/protected';
import getTabletUnprotectedRoutes from 'routes/tablet/unprotected';

interface RouterProps {
  isSpinnerActive: boolean;
  hasChildren: boolean;
  isActiveSubscription: boolean;
  userType: string;
}

const MainRouter: React.FC<RouterProps> = ({
  isActiveSubscription,
  isSpinnerActive,
  hasChildren,
  userType,
}) => {
  const location = useLocation();
  const isSignedIn = useSelector(isSignedInSelector);
  const isNewUser = useSelector(isNewUserSelector);
  const activeUser = isActiveSubscription || userType === 'teacher';

  const isHeaderVisible = useMemo(
    () =>
      ![
        '/login',
        '/sign-up',
        '/welcome',
        '/choose-role',
        '/process-steps',
        '/сongratulations',
        '/process-steps-admin',
        '/expired-subscription',
      ].includes(location.pathname),
    [location.pathname]
  );

  const allowRedirect = useMemo(
    () =>
      ['onboarding', 'map', 'next-room', 'digital-map', 'dashboard'].includes(
        location.pathname.split('/')[1]
      ),
    [location.pathname]
  );

  const rootRedirect = useMemo(() => {
    if (isSignedIn && userType) {
      if (userType === 'teacher') {
        return '/process-steps-admin';
      }
      if (isActiveSubscription && userType === 'parent') {
        if (MobileService.isMobile) {
          return '/map';
        }
        return isNewUser ? '/process-steps' : '/map';
      }
      return MobileService.isMobile
        ? '/expired-subscription'
        : '/dashboard/billing';
    }
    return MobileService.isMobile ? '/login' : '/choose-role';
  }, [isSignedIn, userType, isActiveSubscription, isNewUser]);

  function getSignedRoutes() {
    if (MobileService.isMobile) {
      return getTabletProtectedRoutes(userType, isActiveSubscription);
    }
    return getDesktopProtectedRoutes(
      hasChildren,
      userType,
      isActiveSubscription
    );
  }

  function getUnsignedRoutes() {
    if (MobileService.isMobile) {
      return getTabletUnprotectedRoutes();
    }

    return getDesktopUnprotectedRoutes();
  }

  return (
    <>
      {isHeaderVisible && <Header />}
      <Switch>
        {isSignedIn ? getSignedRoutes() : getUnsignedRoutes()}
        <Redirect
          to={`${
            allowRedirect && activeUser && isSignedIn
              ? location.pathname
              : rootRedirect
          }`}
        />
      </Switch>
      {isSpinnerActive && <Spinner />}
    </>
  );
};

export default MainRouter;
